import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { request, type DashboardResponse, type DashboardTxn } from "../lib/api";

/**
 * Dashboard – React SPA equivalent of Laravel Blade dashboard
 * Mirrors sections, layout, and styles 1:1.
 * Adds compliance banners (KYC/KYB/UBO) with dismiss buttons.
 */
// robust date-only formatter
function fmtDate(d: unknown): string {
  if (!d) return "—";
  if (typeof d === "string") {
    // Grab leading YYYY-MM-DD if present (works for ISO and "YYYY-MM-DD HH:MM:SS")
    const m = d.match(/^\d{4}-\d{2}-\d{2}/);
    if (m) return m[0];
  }
  const dt = new Date(d as any);
  return isNaN(dt.getTime()) ? "—" : dt.toISOString().slice(0, 10);
}

type BannerKey = "kyc" | "kyb" | "ubo";

function Banner({
  tone = "info",
  title,
  message,
  actionHref,
  actionText = "Continue",
  onDismiss,
}: {
  tone?: "info" | "warning" | "danger";
  title: string;
  message?: string;
  actionHref: string;
  actionText?: string;
  onDismiss?: () => void;
}) {
  const toneClasses =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-blue-200 bg-blue-50 text-blue-800";

  return (
    <div className={`rounded-xl border px-4 py-3 ${toneClasses}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <div className="font-semibold">{title}</div>
          {message && <div className="text-sm mt-0.5 opacity-90">{message}</div>}
        </div>
        <div className="flex items-center gap-2">
          <Link to={actionHref} className="btn btn-white">
            {actionText}
          </Link>
          {onDismiss && (
            <button type="button" className="btn btn-white" onClick={onDismiss}>
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch dashboard payload
  useEffect(() => {
    (async () => {
      try {
        const res = await request<DashboardResponse>("/xapi/dashboard", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        setData(res);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dismissBanner = useCallback(
    async (key: BannerKey) => {
      try {
        await request(`/xapi/profile/${key}-banner/dismiss`, {
          method: "POST",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        // Optimistic local update
        setData((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          if (key === "kyc" && next.kyc) next.kyc.dismissed = true;
          if (key === "kyb" && next.kyb) next.kyb.dismissed = true;
          if (key === "ubo" && next.ubo) next.ubo.dismissed = true;
          return next;
        });
      } catch (err) {
        console.error(`Dismiss ${key} failed:`, err);
      }
    },
    [setData]
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="skeleton h-6 w-32 mb-4" />
        <div className="skeleton h-4 w-48" />
      </div>
    );
  }
  if (!data) return <div className="p-6 text-gray-500">No data available.</div>;

  const {
    availableSums,
    pendingByCurrency,
    kpi,
    cardsCount,
    recipientsCount,
    requestedAccounts,
    recentTransactions,
    kyc,
    kyb,
    ubo,
    modules,
  } = data;

  const moduleEnabled = (name: string, fallback = true) => modules?.[name]?.enabled ?? fallback;
  const accountsEnabled = moduleEnabled("accounts");
  const railsEnabled = moduleEnabled("banking_rails");
  const fxEnabled = moduleEnabled("fx");

  // ✅ KYC banner only when API says it's required
  const kycNeedsAttention =
    !!kyc &&
    kyc.required === true &&
    !kyc.dismissed &&
    (kyc.status ?? "not_started") !== "approved";

  // KYB/UBO already respect "applicable" in API
  const kybStatus = kyb?.status ?? "not_started";
  const kybNeedsAttention =
    !!kyb && kyb.applicable && !kyb.dismissed && kybStatus !== "approved";

  const uboNeedsAttention =
    !!ubo && ubo.applicable && !ubo.dismissed && (ubo.status ?? "not_started") !== "approved";

  return (
    <div className="px-4 md:px-8 space-y-6">
      {/* --- Compliance banners --- */}
      {(kycNeedsAttention || kybNeedsAttention || uboNeedsAttention) && (
        <div className="space-y-3">
          {kycNeedsAttention && (
            <Banner
              tone="info"
              title={
                kyc.status === "rejected"
                  ? "KYC rejected – action required"
                  : kyc.status === "pending"
                  ? "KYC under review"
                  : "KYC not completed"
              }
              message={
                kyc.status === "rejected"
                  ? "Your identity verification was rejected. Please resubmit your documents."
                  : kyc.status === "pending"
                  ? "We’re reviewing your documents. You can update details if requested."
                  : "Please complete your identity verification to unlock transfers and accounts."
              }
              actionHref="/kyc"
              actionText={kyc.status === "pending" ? "View status" : "Start KYC"}
              onDismiss={() => dismissBanner("kyc")}
            />
          )}
          {kybNeedsAttention && (
            <Banner
              tone="warning"
              title={
                kybStatus === "rejected"
                  ? "KYB rejected – business verification needed"
                  : kybStatus === "pending"
                  ? "KYB under review"
                  : "Complete KYB for your business"
              }
              message={
                kybStatus === "rejected"
                  ? "Your business verification was rejected. Open KYB to address issues."
                  : kybStatus === "pending"
                  ? "We’re reviewing your KYB submission."
                  : "To use business features, complete KYB verification."
              }
              actionHref={
                kybStatus === "not_started"
                  ? "/kyb/create"
                  : kybStatus === "rejected" && kyb?.id
                  ? `/kyb/${kyb.id}/edit`
                  : "/kyb" // pending or approved → status page
              }
              actionText={
                kybStatus === "not_started"
                  ? "Start KYB"
                  : kybStatus === "rejected"
                  ? "Fix KYB"
                  : "View status"
              }
              onDismiss={() => dismissBanner("kyb")}
            />
          )}
          {uboNeedsAttention && (
            <Banner
              tone="warning"
              title={
                ubo.status === "rejected"
                  ? "UBO review rejected"
                  : ubo.status === "pending"
                  ? "UBO review pending"
                  : "Add UBOs (Ultimate Beneficial Owners)"
              }
              message={
                ubo.status === "rejected"
                  ? "One or more UBO submissions were rejected. Please review and update."
                  : ubo.status === "pending"
                  ? "We’re reviewing UBOs and their documents."
                  : "Add all beneficial owners and upload their documents."
              }
              actionHref="/profile/ubos"
              actionText={ubo.status === "pending" ? "View UBOs" : "Manage UBOs"}
              onDismiss={() => dismissBanner("ubo")}
            />
          )}
        </div>
      )}

      {/* --- Top KPI cards --- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
        {/* Total Funding Balance */}
        <div className="card-box p-4">
          <div className="text-sm text-gray-500">Total Funding Balance</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.keys(availableSums || {}).length === 0 ? (
              <span className="text-sm text-gray-500">No funding accounts yet</span>
            ) : (
              Object.entries(availableSums).map(([cur, amt]) => {
                const blocked = pendingByCurrency?.[cur] || 0;
                return (
                  <span
                    key={cur}
                    className="inline-flex items-center rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-1 text-sm"
                  >
                    <span className="font-semibold mr-1">{cur}</span>
                    <span>
                      {Number(amt ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    {blocked > 0 && (
                      <span className="ml-2 text-xs text-amber-600">
                        (-{/* plain minus, no en-dash */}
                        {Number(blocked ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        pending)
                      </span>
                    )}
                  </span>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Transfers */}
        <div className="card-box p-4">
          <div className="text-sm text-gray-500">Recent Transfers</div>
          <div className="mt-2 text-2xl font-semibold">{kpi?.recentTransfers ?? 0}</div>
          <div className="mt-1 text-xs text-gray-500">Last updates</div>
        </div>

        {fxEnabled && (
          <div className="card-box p-4">
            <div className="text-sm text-gray-500">FX Conversions</div>
            <div className="mt-2 text-2xl font-semibold">{kpi?.recentFx ?? 0}</div>
            <div className="mt-1 text-xs text-gray-500">Last updates</div>
          </div>
        )}

        {/* Your Setup */}
        <div className="card-box p-4">
          <div className="text-sm text-gray-500">Your Setup</div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <div className="text-2xl font-semibold">{cardsCount ?? 0}</div>
              <div className="mt-1 text-xs text-gray-500">Cards</div>
            </div>
            <div>
              <div className="text-2xl font-semibold">{recipientsCount ?? 0}</div>
              <div className="mt-1 text-xs text-gray-500">Recipients</div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Quick actions --- */}
      <div className="flex flex-wrap items-center gap-2">
        {railsEnabled && (
          <Link to="/funding" className="btn">
            <img src="/icons/cards.svg" className="w-4 h-4" /> <span className="ml-1">Add funds</span>
          </Link>
        )}
        {/* 🔁 FIXED: point to /transfers/create so it loads TransferCreate.tsx */}
        {railsEnabled && (
          <Link to="/transfers/create" className="btn">
            <img src="/icons/transfers.svg" className="w-4 h-4" />{" "}
            <span className="ml-1">Make a transfer</span>
          </Link>
        )}
        {fxEnabled && (
          <Link to="/fx/create" className="btn">
            <img src="/icons/transactions.svg" className="w-4 h-4" />{" "}
            <span className="ml-1">Exchange</span>
          </Link>
        )}
        {railsEnabled && (
          <Link to="/recipients" className="btn">
            <img src="/icons/user.svg" className="w-4 h-4" /> <span className="ml-1">Recipients</span>
          </Link>
        )}
      </div>

      {/* --- Accounts snapshot --- */}
      <div className="section-box p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h3 className="font-semibold">Funding Accounts</h3>
          {accountsEnabled && (
            <Link to="/accounts" className="text-sm text-gray-500 hover:text-gray-700">
              View all
            </Link>
          )}
        </div>
        <div className="p-4 space-y-4">
          {/* Requested accounts */}
          {requestedAccounts &&
            requestedAccounts.filter((a) => a.status === "pending").length > 0 && (
              <div className="rounded-xl border border-amber-200 px-3 py-2 text-sm">
                <div className="font-medium mb-1">Requested accounts</div>
                <div className="flex flex-wrap gap-2">
                  {requestedAccounts
                    .filter((a) => a.status === "pending")
                    .map((acc) => (
                      <span
                        key={`${acc.currency}-${acc.requested_at ?? ""}`}
                        className="inline-flex items-center rounded-xl border border-amber-200 px-3 py-1 text-sm"
                      >
                        <span className="font-semibold mr-1">{acc.currency}</span>
                        <span className="inline-flex items-center rounded-md border border-amber-200 text-amber-700 px-1.5 py-0.5 text-xs">
                          Requested
                        </span>
                      </span>
                    ))}
                </div>
              </div>
            )}

          {/* Approved balances */}
          {Object.keys(availableSums || {}).length === 0 ? (
            <div className="text-sm text-gray-500">
              No funding accounts yet.{" "}
              {accountsEnabled ? (
                <Link className="underline" to="/accounts/request">
                  Request an account
                </Link>
              ) : (
                "Accounts are not available"
              )}
              .
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(availableSums).map(([cur, avail]) => {
                const blocked = pendingByCurrency?.[cur] || 0;
                return (
                  <div
                    key={cur}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{cur} Account</div>
                      <Link
                        to="/accounts"
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Details
                      </Link>
                    </div>
                    <div className="mt-2 text-2xl font-semibold">
                      {Number(avail ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      <span className="text-base font-normal">{cur}</span>
                    </div>
                    {blocked > 0 && (
                      <div className="text-xs text-amber-600 mt-1">
                        Pending holds:{" "}
                        {Number(blocked ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        {cur}
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      {railsEnabled && (
                        <Link to="/funding" className="btn btn-white">
                          Deposit
                        </Link>
                      )}
                      {/* 🔁 FIXED: also point to /transfers/create here */}
                      {railsEnabled && (
                        <Link to="/transfers/create" className="btn btn-white">
                          Transfer
                        </Link>
                      )}
                      {fxEnabled && (
                        <Link to="/fx/create" className="btn btn-white">
                          FX
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* --- Recent Activity --- */}
      <div className="section-box p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h3 className="font-semibold">Recent Activity</h3>
          <Link to="/transactions" className="text-sm text-gray-500 hover:text-gray-700">
            View transactions
          </Link>
        </div>
        <div className="p-4 overflow-x-auto">
          {!recentTransactions || recentTransactions.length === 0 ? (
            <div className="text-sm text-gray-500">No recent transactions.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="table-header">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Currency</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {recentTransactions.map((t: DashboardTxn) => {
                  const rawAmount = Number(t.amount ?? 0);
                  const isIncoming = t.direction === "incoming";
                  const sign = isIncoming ? "+" : "-";
                  const displayAmount = Math.abs(rawAmount);

                  return (
                    <tr key={t.id}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {fmtDate(t.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            isIncoming ? "text-green-600" : "text-red-600"
                          }
                        >
                          {sign}
                          {displayAmount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </td>
                      <td className="px-3 py-2">{t.currency ?? "—"}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-lg px-2 py-0.5 text-xs border ${
                            t.status === "executed"
                              ? "border-green-200 text-green-700 dark:border-green-700"
                              : t.status === "pending"
                              ? "border-amber-200 text-amber-700 dark:border-amber-700"
                              : t.status === "failed"
                              ? "border-red-200 text-red-700 dark:border-red-700"
                              : "border-gray-200 text-gray-600 dark:border-gray-700"
                          }`}
                        >
                          {t.status
                            ? t.status.charAt(0).toUpperCase() + t.status.slice(1)
                            : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Tip: Use <span className="font-medium">Recipients</span> to save beneficiary details and
        speed up transfers.
      </div>
    </div>
  );
}
